// @file Establishes connection with Brahms.
import window from '@@/bits/global'
import * as Phoenix from 'phoenix'

const MESSAGE_QUEUE: any[] = []
const RECONNECT_DELAY = [50, 75, 100, 200, 500, 1000, 2000, 5000]
let connectionRetries = 0

const getSocket = (accessToken: string, deviceId: string): Phoenix.Socket | null => {
  try {
    const realtimeTag = document.getElementsByName('realtime-uri')[0] as HTMLMetaElement
    return new Phoenix.Socket(realtimeTag.content, {
      params: { accessToken, deviceId },
      longpollerTimeout: 31000,
      reconnectAfterMs: (tries: number) => {
        connectionRetries++
        return RECONNECT_DELAY[tries - 1] || 10000
      },
    })
  } catch (e) {
    return null
  }
}

const subscribeToChannel = (socket: Phoenix.Socket, topic: string): [Phoenix.Channel, number] | [null, null] => {
  try {
    const channel = socket.channel(topic)
    const ref = channel.on('new_msg', (data) => {
      MESSAGE_QUEUE.push(data)
    })
    channel.join()
    return [channel, ref]
  } catch (e) {
    return [null, null]
  }
}

const subscribeToWallBrahmsChannel = (
  socket: Phoenix.Socket,
  wallId: string,
): [Phoenix.Channel, number] | [null, null] => {
  return subscribeToChannel(socket, `wall:${wallId}`)
}

const subscribeToDeviceBrahmsChannel = (
  socket: Phoenix.Socket,
  deviceId: string,
): [Phoenix.Channel, number] | [null, null] => {
  return subscribeToChannel(socket, `device:${deviceId}`)
}

const subscribeToUserBrahmsChannel = (socket: Phoenix.Socket, userId: string): Phoenix.Channel | null => {
  try {
    const userChannel = socket.channel(`user:${userId}`)
    userChannel.join()
    return userChannel
  } catch (e) {
    return null
  }
}

const initializeBrahmsConnection = (): void => {
  const { accessToken, wallId, userId, deviceId } = window.ww.brahmsStartingState
  if (!accessToken || !wallId || !userId || !deviceId) return

  if (!window.ww) window.ww = {}
  if (window.ww.brahms == null) window.ww.brahms = {}

  // Initiate connection with Brahms
  const socket = getSocket(accessToken, deviceId)
  if (socket == null) return
  socket?.onError((_, transport, establishedConnections) => {
    if (transport === WebSocket && establishedConnections === 0 && connectionRetries > 1) {
      socket?.replaceTransport(Phoenix.LongPoll)
      socket?.connect()
      window.ww.brahms = { ...window.ww.brahms, isUsingLongPolling: true }
    }
  })
  socket.connect()

  // Connect to wall channel
  const [wallBrahmsChannel, wallBrahmsChannelMsgRef] = subscribeToWallBrahmsChannel(socket, wallId)
  if (wallBrahmsChannel == null || wallBrahmsChannelMsgRef == null) return

  // Connect to device channel
  const [deviceBrahmsChannel, deviceBrahmsChannelMsgRef] = subscribeToDeviceBrahmsChannel(socket, deviceId)
  if (deviceBrahmsChannel == null || deviceBrahmsChannelMsgRef == null) return

  // Connect to user channel
  const userBrahmsChannel = subscribeToUserBrahmsChannel(socket, userId)
  if (userBrahmsChannel == null) return

  window.ww.brahms = {
    ...window.ww.brahms,
    socket,
    // Phoenix doesn't allow us to get the reference of
    // an already established subscription on a channel, so we need to
    // pass all the references.
    channels: {
      wall: wallBrahmsChannel,
      device: deviceBrahmsChannel,
      user: userBrahmsChannel,
    },
    // We want to remove the listeners defined on this file
    // once we get to the `realtime` store, so we need to
    // pass the handler references.
    msgRefs: {
      wall: wallBrahmsChannelMsgRef,
      device: deviceBrahmsChannelMsgRef,
    },
    // Received messages will not be queued if a handler is
    // not defined, so we pass a message queue that the
    // `realtime` store will clear once other handlers are added.
    messageQueue: MESSAGE_QUEUE,
  }
}

initializeBrahmsConnection()
